/** Przypisanie serii wykresu do osi Y (lewa / prawa liczby / prawa PLN). */

export type ChartAxisId = "left" | "right" | "rightMoney";

export type ChartSeriesInput = {
  key: string;
  label: string;
  color?: string;
  rightAxis?: boolean;
  format?: "number" | "pln" | "usd";
  stackId?: string;
};

export type ResolvedChartSeries = ChartSeriesInput & {
  axisId: ChartAxisId;
};

const RATIO_THRESHOLD = 0.2;

export function maxForKey(data: Record<string, unknown>[], key: string): number {
  let max = 0;
  for (const row of data) {
    const v = Number(row[key] ?? 0);
    if (!Number.isNaN(v) && v > max) max = v;
  }
  return max;
}

function isStackedChart(series: ChartSeriesInput[]): boolean {
  return series.some((s) => Boolean(s.stackId));
}

export function inferChartAxes(
  series: ChartSeriesInput[],
  data: Record<string, unknown>[],
): ResolvedChartSeries[] {
  if (!series.length) return [];

  if (isStackedChart(series)) {
    return series.map((s) => ({ ...s, axisId: "left" as ChartAxisId }));
  }

  const resolved: ResolvedChartSeries[] = series.map((s) => {
    if (s.format === "pln") {
      return { ...s, axisId: "rightMoney" };
    }
    if (s.rightAxis) {
      return { ...s, axisId: "right" };
    }
    return { ...s, axisId: "left" };
  });

  const numeric = resolved.filter((s) => s.format !== "pln" && !s.rightAxis);
  if (numeric.length < 2) {
    return resolved;
  }

  const maxByKey = new Map<string, number>();
  for (const s of numeric) {
    maxByKey.set(s.key, maxForKey(data, s.key));
  }

  const sorted = [...numeric].sort(
    (a, b) => (maxByKey.get(b.key) ?? 0) - (maxByKey.get(a.key) ?? 0),
  );
  const largestMax = maxByKey.get(sorted[0].key) ?? 0;

  if (largestMax <= 0) {
    return resolved;
  }

  for (const s of sorted.slice(1)) {
    const max = maxByKey.get(s.key) ?? 0;
    if (max <= 0) continue;
    if (max / largestMax < RATIO_THRESHOLD) {
      const idx = resolved.findIndex((r) => r.key === s.key);
      if (idx >= 0 && resolved[idx].axisId === "left") {
        resolved[idx] = { ...resolved[idx], axisId: "right" };
      }
    }
  }

  const hasLeft = resolved.some((s) => s.axisId === "left");
  if (!hasLeft) {
    const firstNumeric = resolved.find((s) => s.format !== "pln");
    if (firstNumeric) {
      const idx = resolved.findIndex((r) => r.key === firstNumeric.key);
      resolved[idx] = { ...resolved[idx], axisId: "left" };
    }
  }

  return resolved;
}

export function axisLegendHint(resolved: ResolvedChartSeries[]): string | undefined {
  const left = resolved.filter((s) => s.axisId === "left").map((s) => s.label);
  const right = resolved.filter((s) => s.axisId === "right").map((s) => s.label);
  const money = resolved.filter((s) => s.axisId === "rightMoney").map((s) => s.label);

  const parts: string[] = [];
  if (left.length) parts.push(`Lewa oś: ${left.join(", ")}`);
  if (right.length) parts.push(`Prawa oś: ${right.join(", ")}`);
  if (money.length) parts.push(`Prawa (zł): ${money.join(", ")}`);

  if (parts.length <= 1) return undefined;
  return parts.join(" · ");
}

export function chartRightMargin(resolved: ResolvedChartSeries[]): number {
  const hasMoney = resolved.some((s) => s.axisId === "rightMoney");
  const hasRight = resolved.some((s) => s.axisId === "right");
  if (hasMoney && hasRight) return 72;
  if (hasMoney) return 52;
  if (hasRight) return 36;
  return 4;
}

export function formatAxisMoney(v: number): string {
  if (v >= 1000) return `${Math.round(v / 100) / 10}k`;
  return String(Math.round(v));
}

export function rawKey(key: string): string {
  return `${key}__raw`;
}

export function normalizeDataToPercent<T extends Record<string, unknown>>(
  data: T[],
  series: ChartSeriesInput[],
): { data: T[]; maxByKey: Record<string, number> } {
  const maxByKey: Record<string, number> = {};
  for (const s of series) {
    maxByKey[s.key] = maxForKey(data, s.key);
  }
  const mapped = data.map((row) => {
    const next: Record<string, unknown> = { ...row };
    for (const s of series) {
      const max = maxByKey[s.key] || 0;
      const raw = Number(row[s.key] ?? 0);
      next[rawKey(s.key)] = Number.isNaN(raw) ? 0 : raw;
      next[s.key] = max > 0 && !Number.isNaN(raw) ? (raw / max) * 100 : 0;
    }
    return next as T;
  });
  return { data: mapped, maxByKey };
}

export function formatAxisPercent(v: number): string {
  return `${Math.round(v)}%`;
}
